'use client';

import AppLayout from '@/components/AppLayout';
import { Wrench, Music2, UserPlus, AlertTriangle, CheckCircle } from 'lucide-react';

interface Notification {
  id: string;
  type: 'maintenance' | 'string-change' | 'user' | 'alert';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: 'notif-001',
    type: 'maintenance',
    title: 'Maintenance Overdue',
    message: 'Gibson 1959 Les Paul Junior has not had maintenance in over 180 days.',
    time: '2 hours ago',
    read: false,
  },
  {
    id: 'notif-002',
    type: 'string-change',
    title: 'String Change Due',
    message: 'Martin D-28 Authentic 1937 strings are due for a change.',
    time: '5 hours ago',
    read: false,
  },
  {
    id: 'notif-003',
    type: 'alert',
    title: 'Guitar On Loan',
    message: 'PRS Custom 24 Wood Library is on loan to Dmitri Volkov. Expected return: Jun 2026.',
    time: '1 day ago',
    read: false,
  },
  {
    id: 'notif-004',
    type: 'user',
    title: 'New User Joined',
    message: 'Sofia Reyes joined GuitarVault as a Collector.',
    time: '2 days ago',
    read: true,
  },
  {
    id: 'notif-005',
    type: 'maintenance',
    title: 'Setup Reminder',
    message: 'Gibson Les Paul Junior needs fret leveling — noted in guitar details.',
    time: '3 days ago',
    read: true,
  },
  {
    id: 'notif-006',
    type: 'string-change',
    title: 'String Change Logged',
    message: 'Ibanez RG550 Genesis Collection string change recorded on 04/02/2026.',
    time: '6 days ago',
    read: true,
  },
];

const typeConfig = {
  maintenance: { icon: Wrench, color: 'text-amber-600 bg-amber-50' },
  'string-change': { icon: Music2, color: 'text-blue-600 bg-blue-50' },
  user: { icon: UserPlus, color: 'text-green-600 bg-green-50' },
  alert: { icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
};

export default function NotificationsPage() {
  const unread = mockNotifications.filter((n) => !n.read).length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {unread > 0 ? `${unread} unread notification${unread > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          {unread > 0 && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary text-primary-foreground">
              {unread} New
            </span>
          )}
        </div>

        {/* Notifications List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border">
          {mockNotifications.map((notif) => {
            const cfg = typeConfig[notif.type];
            const NotifIcon = cfg.icon;
            return (
              <div
                key={notif.id}
                className={`flex items-start gap-4 px-5 py-4 transition-colors hover:bg-muted/30 ${
                  !notif.read ? 'bg-primary/5' : ''
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.color}`}>
                  <NotifIcon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{notif.title}</p>
                    {!notif.read && (
                      <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{notif.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{notif.time}</p>
                </div>
                {notif.read && (
                  <CheckCircle size={14} className="text-green-500 shrink-0 mt-1" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
